import { useState, useEffect, useCallback, useMemo } from 'react';
import { DataTable, DataTableColumn } from '../../components/ui/DataTable';
import { Card } from '../../components/ui/Card';
import { useAccounts } from '../../hooks/useAccounts';
import { exportToExcel } from '../../utils/export/exportUtils';
import { Download, Search, BookOpen } from 'lucide-react';
import type { Account } from '../../types/accounting';

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  asset: 'Actif',
  liability: 'Passif',
  equity: 'Capitaux propres',
  revenue: 'Produit',
  expense: 'Charge',
};

const CLASS_LABELS: Record<string, string> = {
  '1': 'Classe 1 — Comptes de ressources durables',
  '2': 'Classe 2 — Comptes d\'actif immobilisé',
  '3': 'Classe 3 — Comptes de stocks',
  '4': 'Classe 4 — Comptes de tiers',
  '5': 'Classe 5 — Comptes de trésorerie',
  '6': 'Classe 6 — Comptes de charges',
  '7': 'Classe 7 — Comptes de produits',
  '8': 'Classe 8 — Comptes des autres charges et produits',
  '9': 'Classe 9 — Comptes analytiques',
};

export default function AccountsPage() {
  const { accounts, loading, loadAccounts } = useAccounts();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [classFilter, setClassFilter] = useState<string>('all');

  useEffect(() => {
    loadAccounts({ pageSize: 500 });
  }, []);

  const filteredAccounts = useMemo(() => {
    let list = accounts || [];
    if (typeFilter !== 'all') {
      list = list.filter(a => a.type === typeFilter);
    }
    if (classFilter !== 'all') {
      list = list.filter(a => (a.class || a.code?.charAt(0)) === classFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        a.code?.toLowerCase().includes(q) ||
        a.name?.toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
  }, [accounts, typeFilter, classFilter, search]);

  const columns: DataTableColumn<Account>[] = [
    {
      header: 'Code',
      accessor: (a: Account) => (
        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{a.code}</span>
      ),
      width: 130,
      sortable: true,
    },
    {
      header: 'Libellé',
      accessor: (a: Account) => a.name,
      sortable: true,
    },
    {
      header: 'Type',
      accessor: (a: Account) => (
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          a.type === 'asset' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
          a.type === 'liability' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
          a.type === 'equity' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
          a.type === 'revenue' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
          a.type === 'expense' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
          'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
        }`}>
          {ACCOUNT_TYPE_LABELS[a.type] || a.type}
        </span>
      ),
      width: 150,
      sortable: true,
    },
    {
      header: 'Classe',
      accessor: (a: Account) => a.class || a.code?.charAt(0) || '-',
      width: 80,
      sortable: true,
    },
    {
      header: 'Standard',
      accessor: (a: Account) => a.standard || 'SYSCOHADA',
      width: 120,
    },
    {
      header: 'Statut',
      accessor: (a: Account) => (
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          a.active !== false
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'
        }`}>
          {a.active !== false ? 'Actif' : 'Inactif'}
        </span>
      ),
      width: 90,
    },
  ];

  const handleExportXLSX = useCallback(() => {
    const rows = filteredAccounts.map(a => ({
      Code: a.code || '',
      'Libellé': a.name || '',
      Type: ACCOUNT_TYPE_LABELS[a.type] || a.type || '',
      Classe: a.class || a.code?.charAt(0) || '',
      Standard: a.standard || 'SYSCOHADA',
      Statut: a.active !== false ? 'Actif' : 'Inactif',
      Description: a.description || '',
    }));
    exportToExcel(
      rows,
      ['Code', 'Libellé', 'Type', 'Classe', 'Standard', 'Statut', 'Description'],
      `plan_comptable_${new Date().toISOString().slice(0, 10)}`
    );
  }, [filteredAccounts]);

  const stats = useMemo(() => {
    const total = filteredAccounts.length;
    const active = filteredAccounts.filter(a => a.active !== false).length;
    return { total, active };
  }, [filteredAccounts]);

  return (
    <div className="space-y-4 p-4">
      <Card
        title={
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <BookOpen className="h-5 w-5 text-primary" />
              <div>
                <span className="font-semibold text-lg">Plan Comptable</span>
                <span className="ml-2 text-xs text-gray-500">
                  {stats.total} comptes ({stats.active} actifs)
                </span>
              </div>
            </div>
            <button
              onClick={handleExportXLSX}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
              title="Exporter le plan comptable en XLSX"
            >
              <Download className="h-3.5 w-3.5" />
              Exporter XLSX
            </button>
          </div>
        }
      >
        {/* Filtres */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par code ou libellé..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
            />
          </div>
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
          >
            <option value="all">Tous les types</option>
            {Object.entries(ACCOUNT_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            value={classFilter}
            onChange={e => setClassFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600"
          >
            <option value="all">Toutes les classes</option>
            {Object.entries(CLASS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        <DataTable<Account>
          columns={columns}
          data={filteredAccounts}
          loading={loading}
          emptyMessage="Aucun compte trouvé"
          emptyTitle="Plan comptable vide"
          showToolbar={false}
          showPagination={true}
          pageSize={50}
          enableResize={true}
          enableSort={true}
          storageKey="accounts-page"
          maxHeight="calc(100vh - 350px)"
          density="compact"
        />
      </Card>
    </div>
  );
}
